import { useState } from 'react';
import { Head } from '@inertiajs/react';
import { MainLayout } from '../../layouts/MainLayout';
import { CartSummary } from './CartSummary';
import { MinOrderAlert } from '../cart/MinOrderAlert';
import { getCSRFToken } from '../../utils/csrf';
import { trackBeginCheckout } from '../../utils/tracking';
import { route } from '../../utils/route';
import type { QuickCheckoutDefaults, CheckoutCart, CheckoutStep, GaEcommerce } from '../../types/checkout';

declare const dataLayer: unknown[];

interface QuickCheckoutViewProps {
  quickCheckout: QuickCheckoutDefaults;
  cart: CheckoutCart;
  cellphone: string;
  onModify: (step?: CheckoutStep) => void;
  ga: GaEcommerce;
  productsPageUrl: string;
}

export function QuickCheckoutView({
  quickCheckout,
  cart,
  cellphone,
  onModify,
  ga,
  productsPageUrl,
}: QuickCheckoutViewProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [comment, setComment] = useState('');

  // Min order validation
  const minOrder = cart.minOrder ?? 0;
  const cartSubtotal = cart.subtotal ?? cart.total;
  const hasUnfulfilledMinimum = minOrder > 0 && cartSubtotal < minOrder;

  // Calculate grand total
  const grandTotal = cart.subtotal != null
    ? (cart.subtotal - (cart.discount || 0) - (cart.subscriptionDiscount || 0) - (cart.replacementCreditDiscount || 0) + cart.deliveryPrice)
    : (cart.total + cart.deliveryPrice);

  const handleSubmit = async () => {
    if (hasUnfulfilledMinimum) return;
    setIsSubmitting(true);
    setSubmitError(null);

    // Fire begin_checkout event (helper async, no bloquea el submit)
    try {
      trackBeginCheckout(ga);
    } catch (e) {
      console.error('Error tracking begin_checkout:', e);
    }

    // Pack IDs from localStorage
    let packIds: number[] = [];
    try {
      const stored = localStorage.getItem('cart_packs');
      if (stored) packIds = Object.keys(JSON.parse(stored)).map(Number);
    } catch (_) {}

    try {
      const res = await fetch(route('api.checkout.order.submit'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-XSRF-TOKEN': getCSRFToken(),
        },
        body: JSON.stringify({
          address_id: quickCheckout.addressId,
          delivery_date: quickCheckout.deliveryDate,
          payment_method: quickCheckout.paymentMethod,
          cellphone: cellphone,
          comment,
          pack_ids: packIds,
          checkout_type: 'one_click',
          ...(quickCheckout.billingCompanyId ? { billing_company_id: quickCheckout.billingCompanyId } : {}),
        }),
      });

      const result = await res.json();

      if (result.success && result.redirect) {
        window.location.href = result.redirect;
      } else {
        setSubmitError(result.message || 'Error al crear el pedido');
        setIsSubmitting(false);
      }
    } catch (err) {
      setSubmitError('Error de conexión. Intentá de nuevo.');
      setIsSubmitting(false);
    }
  };

  return (
    <MainLayout>
      <Head title="Checkout rápido">
        <meta name="robots" content="noindex, nofollow" />
      </Head>

      <div className="mx-auto max-w-[600px] px-4 pb-28 pt-6">
        <div className="py-4 text-center">
          <h1 className="m-0 text-2xl font-bold text-gray-800">Checkout rápido</h1>
          <p className="mt-2 text-sm text-gray-500">
            Elegimos las mejores opciones para vos.
            <br />
            Confirmá para finalizar o modificá para elegir otras opciones.
          </p>
        </div>

        {/* Cart Summary */}
        <CartSummary cart={cart} productsPageUrl={productsPageUrl} />

        {hasUnfulfilledMinimum && (
          <MinOrderAlert minOrder={minOrder} currentTotal={cartSubtotal} />
        )}

        {/* Pre-selected options */}
        <div className="mb-4 rounded-xl border border-gray-200 bg-white shadow-sm">
          {/* Billing Info */}
          {quickCheckout.billing && (
            <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-3.5">
              <span className="text-green-500">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
              </span>
              <div className="min-w-0 flex-1">
                <span className="block text-xs text-gray-400">Facturación</span>
                <span className="block truncate text-[15px] text-gray-800">{quickCheckout.billing.companyName}</span>
                {quickCheckout.billing.cuit && (
                  <span className="block text-xs text-gray-500">
                    CUIT {quickCheckout.billing.cuit}{quickCheckout.billing.ivaCondition ? ` · ${quickCheckout.billing.ivaCondition}` : ''}
                  </span>
                )}
              </div>
              <button type="button" onClick={() => onModify('billing')} className="p-1.5 text-green-400 transition-colors hover:text-green-600">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </button>
            </div>
          )}

          {/* Address */}
          <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-3.5">
            <span className="text-green-500">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            </span>
            <div className="min-w-0 flex-1">
              <span className="block text-xs text-gray-400">Dirección de entrega</span>
              <span className="block truncate text-[15px] text-gray-800">{quickCheckout.addressDisplay.split(',')[0]}</span>
            </div>
            <button type="button" onClick={() => onModify('address')} className="p-1.5 text-green-400 transition-colors hover:text-green-600">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
          </div>

          {/* Delivery Date */}
          <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-3.5">
            <span className="text-green-500">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </span>
            <div className="min-w-0 flex-1">
              <span className="block text-xs text-gray-400">Fecha de entrega</span>
              <span className="block truncate text-[15px] text-gray-800">{quickCheckout.deliveryDateLabel}</span>
              {quickCheckout.hasAlternativeDates && (
                <button
                  type="button"
                  onClick={() => onModify('deliveryDate')}
                  className="mt-0.5 text-xs font-medium text-green-500 hover:text-green-600"
                >
                  Ver otras fechas disponibles
                </button>
              )}
            </div>
            <button type="button" onClick={() => onModify('deliveryDate')} className="p-1.5 text-green-400 transition-colors hover:text-green-600">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
          </div>

          {/* Payment Method */}
          <div className="flex items-center gap-3 px-4 py-3.5">
            <span className="text-green-500">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                <line x1="1" y1="10" x2="23" y2="10" />
              </svg>
            </span>
            <div className="min-w-0 flex-1">
              <span className="block text-xs text-gray-400">Método de pago</span>
              <span className="block truncate text-[15px] text-gray-800">{quickCheckout.paymentMethodName}</span>
            </div>
            <button type="button" onClick={() => onModify('payment')} className="p-1.5 text-green-400 transition-colors hover:text-green-600">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
          </div>
        </div>

        {/* Observaciones */}
        <div className="mb-4">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Instrucciones especiales para tu pedido..."
            rows={2}
            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-[15px] text-gray-800 shadow-sm placeholder:text-gray-400 focus:border-green-300 focus:outline-none focus:ring-1 focus:ring-green-300"
          />
        </div>

        {/* Total */}
        <div className="mb-4 rounded-xl border border-gray-200 bg-white px-4 py-3.5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-[15px] font-semibold text-gray-800">Total a pagar</span>
            <span className="text-lg font-bold text-green-500">
              ${grandTotal.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
            </span>
          </div>
        </div>

        {/* Error message */}
        {submitError && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
            {submitError}
          </div>
        )}
      </div>

      {/* Fixed bottom actions */}
      <div className="fixed inset-x-0 bottom-0 border-t border-gray-200 bg-white px-4 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pt-3 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
        <div className="mx-auto flex max-w-[600px] gap-3">
          <button
            type="button"
            onClick={() => window.history.back()}
            disabled={isSubmitting}
            className="flex items-center justify-center gap-1.5 flex-1 rounded-xl border border-gray-200 bg-white px-4 py-3.5 text-[15px] font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Volver
          </button>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || hasUnfulfilledMinimum}
            className="flex-1 rounded-xl bg-green-500 px-4 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-green-600 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Procesando...
              </span>
            ) : (
              'Confirmar pedido'
            )}
          </button>
        </div>
      </div>
    </MainLayout>
  );
}
